import { useParams } from 'react-router-dom';

import { useT } from '@/core/I18nProvider';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { getDocPage } from './content/docs-nav';
import { DocBlocks } from './DocBlocks';
import { DocsLayout, type TocHeading } from './DocsLayout';
import { NotFound } from '../not-found';

import type { ReactElement } from 'react';

/**
 * `/docs/*` and `/<locale>/docs/*` page route (ADR-0074/0075; site-wide locales
 * ADR-0133). The page comes from the splat slug; an unknown slug renders the 404
 * page (no silent fallback). The locale comes from the active i18n context, which
 * `LocaleSync` keeps pinned to the URL prefix — the router only matches a valid
 * locale segment. Owns the page head: per-locale title/description/canonical +
 * `hreflang` alternates.
 */
export function DocPage(): ReactElement {
  const { t, locale } = useT();
  const params = useParams();
  // Production serves directory indexes behind a 301 to the trailing-slash URL
  // (Amplify/CloudFront), so the splat arrives as "introduction/" — normalize,
  // or every docs deep link 404s after hydration (launch-readiness B2 find).
  const slug = (params['*'] ?? '').replace(/\/+$/, '');

  const page = slug.length > 0 ? getDocPage(slug) : undefined;

  // Always call the hook (Rules of Hooks). Describe the real page when resolved;
  // fall back to the docs index meta for the 404 branch (no misleading canonical).
  const enRelative = page !== undefined ? `/docs/${page.slug}` : '/docs';
  useLocalizedSeoMeta({
    title: page !== undefined ? t(page.titleKey) : t('docs.index.metaTitle'),
    description: page !== undefined ? t(page.descriptionKey) : t('docs.index.metaDescription'),
    path: enRelative,
    type: 'article',
  });

  if (page === undefined) {
    return <NotFound />;
  }

  const headings: TocHeading[] = page.blocks
    .filter(
      (b): b is Extract<typeof b, { kind: 'heading' }> => b.kind === 'heading' && b.level === 2,
    )
    .map((h) => ({ id: h.id, textKey: h.textKey }));

  return (
    <DocsLayout locale={locale} currentSlug={page.slug} headings={headings}>
      <article className="flex flex-col gap-6">
        <header className="flex flex-col gap-3">
          {/* The landing's display scale, two steps down: an article title sits
              under the page's own hierarchy, not level with a hero. */}
          <h1 className="text-[clamp(1.85rem,3.2vw,2.5rem)] font-semibold leading-[1.08] tracking-[-0.042em] text-[color:var(--bd-ink)]">
            {t(page.titleKey)}
          </h1>
          <p className="text-pretty text-[1.08rem] leading-[1.6] text-[color:var(--bd-muted)]">
            {t(page.descriptionKey)}
          </p>
          {/*
            The brand rule, at article scale.

            `PageHero`'s own doc comment gives the reason it carries one: it "is
            what makes ~25 otherwise-identical white mastheads read as belonging
            to the landing page rather than as a default `<h1>` on a blank
            document". That argument was applied to the 25 and never to the
            ~100 docs articles, which are the LARGER family — so the biggest
            section of the site was the one part of it carrying no brand mark
            anywhere on the page. Measured across the built site, every
            marketing route paints some gradient area and every `/docs/*` route
            painted exactly none.

            It is also doing typographic work rather than decoration, which is
            why a docs page earns one at all. The lead and the first body
            paragraph are two paragraphs with a gap between them and nothing
            else — on `/docs/glossary` they run together as one thought — so the
            rule is the boundary that says the standfirst has ended.

            Not a copy of the hero's device: full-bleed there, article-width
            here, and no brand wash, because a tinted ground beside a sidebar
            would read as a highlighted region rather than as a masthead.
          */}
          <span
            aria-hidden="true"
            className="mt-1 h-px w-full"
            style={{ background: 'var(--bd-gradient)', opacity: 0.5 }}
          />
        </header>
        <DocBlocks blocks={page.blocks} t={t} locale={locale} />
      </article>
    </DocsLayout>
  );
}
